import * as RadPopover from '@radix-ui/react-popover'
import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'

import { useTranslation } from '@root/shared/localization'
import { ArrowDown2Icon, ArrowUp2Icon } from '@root/shared/ui-kit'

import {
  USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP,
  USER_TRADING_ANALYTICS_PERIODS,
  type UserTradingAnalyticsPeriod,
} from '../../models/user-trading-analytics-period'

const ARROW_SIZE = 12
const TRIGGER_OPTION_WIDTH = 50

type Props = {
  value: UserTradingAnalyticsPeriod
  onChange: (value: UserTradingAnalyticsPeriod) => void
}

export const PeriodDropdown: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = (item: UserTradingAnalyticsPeriod) => {
    onChange(item)
    setOpen(false)
  }
  const handleTriggerClick = () => setOpen(!open)

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger onClick={handleTriggerClick} asChild>
        <TriggerButton>
          <TriggerOption>
            <Label>{t(USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[value])}</Label>
          </TriggerOption>
          <TriggerArrow>
            {open ? (
              <ArrowUp2Icon size={ARROW_SIZE} />
            ) : (
              <ArrowDown2Icon size={ARROW_SIZE} />
            )}
          </TriggerArrow>
        </TriggerButton>
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="start">
          {USER_TRADING_ANALYTICS_PERIODS.map((item) => (
            <ItemButton
              key={item}
              aria-checked={item === value ? 'true' : 'false'}
              onClick={() => handleSelect(item)}
            >
              <Label>{t(USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[item])}</Label>
            </ItemButton>
          ))}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const contentShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

export const Root = styled(RadPopover.Root)``
export const Trigger = styled(RadPopover.Trigger)``

const TriggerButton = styled.button`
  all: unset;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 2px;
  padding: 3px;
  background: var(--backgroundA-2);
  border-radius: 16px;
`

const TriggerOption = styled.div`
  width: ${TRIGGER_OPTION_WIDTH}px;

  background: var(--primary-2);
  border: 1px solid var(--primary-9);

  border-radius: 17px;
  padding-inline: 16px;
  height: 30px;
  display: grid;
  place-items: center;
  color: var(--text-12);
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
`

const TriggerArrow = styled.div`
  display: grid;
  place-items: center;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  border: 1px solid var(--backgroundA-3);
`

const Label = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-transform: uppercase;
`

export const Content = styled(RadPopover.Content)`
  background: var(--background-2);
  box-shadow: 2px 2px 9px 1px rgb(0 0 0 / 30%);
  backdrop-filter: blur(22.5px);

  overflow: hidden;
  margin-top: -36px;
  z-index: 2;
  padding: 3px;
  gap: 1px;
  display: grid;

  border-radius: 16px;
  animation-duration: 200ms;
  animation-name: ${contentShow};
`

const ItemButton = styled.button`
  width: ${TRIGGER_OPTION_WIDTH}px;
  color: var(--text-11);

  border-radius: 17px;

  background: var(--backgroundA-2);
  padding: 5px 16px;
  border: 1px solid transparent;

  height: 30px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  justify-content: center;
  align-items: center;
  column-gap: 6px;

  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;

  font-size: 12px;
  font-weight: 600;
  line-height: 20px;

  transition: all 0.15s linear;

  &[aria-checked='true'] {
    color: var(--text-12);
    background: var(--primary-2);
    border-color: var(--primary-9);
  }

  &:hover,
  &:focus-visible {
    color: var(--text-12);
    background: var(--backgroundA-3);
  }
`
